import { GetRowIdParams, GridOptions, GridReadyEvent, RowSelectedEvent } from "@ag-grid-community/core";
import { Component, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import {
  BarcodePrintStatusEnum,
  JoDetail,
  JoDetailAddon,
  JoHeader,
  JoPullBatch,
} from "app/model/entities/entity-model";
import { JoStatusCode } from "app/model/enums/jo-status-code";

import { ItemDetailMatchSummary } from "@/model/entities/-item-detail-match-summary";
import { AgFns, GridOptionsExt, RowSelectionMultiple, RowSelectionMultipleCheckbox } from "app/shared/ag-fns";
import { BarcodeService } from "app/shared/barcode.service";
import { DialogBaseComponent } from "app/shared/dialog-base.component";
import { FileViewDialogComponent, FileViewDialogParams, IDoc } from "app/shared/file-view-dialog.component";
import { JoUtilsService } from "app/shared/jo-utils.service";
import { PoUtilsService } from "app/shared/po-utils.service";
import { TemplateRendererComponent } from "app/shared/template-renderer.component";
import _ from "lodash";
import { takeUntil } from "rxjs/operators";
import { DomainBaseComponent, wrapBusy } from "../shared/domain-base.component";
import { DomainService } from "../shared/domain.service";
import { NavFns } from "app/shared/nav-fns";
import { ChangeJoPullBatchDialogComponent } from "./change-jo-pull-batch-dialog.component";

import { UnapplyJodDialogComponent } from "./unapply-jod-dialog.component";

@Component({
  selector: "app-jo-pull-to-wip",
  templateUrl: "./jo-pull-to-wip.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class JoPullToWipComponent extends DomainBaseComponent implements OnInit {
  @ViewChild("deleteJoHeaderFromBatchCell")
  deleteJoHeaderFromBatchCell: TemplateRef<any>;
  @ViewChild("viewDocCell") viewDocCell: TemplateRef<any>;

  shouldPrintBarcodes: boolean = true;
  joHeaders: JoHeader[] = [];
  johGridOptions: GridOptionsExt;

  joDetails: JoDetail[] = [];
  jodGridOptions: GridOptionsExt;

  joPullBatch: JoPullBatch;
  joPullBatchId: number;

  allMatchSummary: ItemDetailMatchSummary[] = [];
  exactMatchSummary: ItemDetailMatchSummary[] = [];

  canMarkComplete: boolean;
  canUncomplete: boolean;
  batchStatusLabel: string;
  inventoryProductTypeMap = new Map<string, Set<number>>();

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    private joUtilsService: JoUtilsService,
    private poUtilsService: PoUtilsService,
    private barcodeService: BarcodeService,
  ) {
    super(domainService);

    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  async updateFromContext() {
    this.johGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onJohGridReady,
      onRowSelected: this.onJohRowSelected,
    });

    this.jodGridOptions = AgFns.initGridOptions(
      this,
      {
        onGridReady: this.onJodGridReady,
        // needed because multiple rows will automatically be selected for each job order that is selected
        // also needed to support the 'Print Barcodes' button
        rowSelection: RowSelectionMultiple,
      },
      { detailProperty: "joDetailAddons" },
    );
    this.updateJodMasterDetail(this.jodGridOptions);

    const joPullBatchId = +this.route.snapshot.params["joPullBatchId"];

    // the promise resolution below is so that we can show the screen before this query completes.
    this.dbQueryService.getItemDetailAllMatchSummaryForJoPullBatch(joPullBatchId).then((r) => {
      this.allMatchSummary = r;
    });
    this.dbQueryService.getItemDetailExactMatchSummaryForJoPullBatch(joPullBatchId).then((r) => {
      this.exactMatchSummary = r;
    });

    var result = this.route.snapshot.queryParams["shouldPrintBarcodes"] == "false";
    this.shouldPrintBarcodes = !result;

    await this.updateForBatch(joPullBatchId);
    this.isPageReady = true;
  }

  async updateForBatch(joPullBatchId: number) {
    this.joPullBatchId = joPullBatchId;
    this.joDetails = await this.dbQueryService.getJoDetailsByJoPullBatchId(this.joPullBatchId);
    this.joHeaders = _.uniq(this.joDetails.map((jod) => jod.joHeader));
    this.updateBatchState();
    await AgFns.refreshGrid(this.johGridOptions, this.joHeaders);

    // Load inventory program productTypeIds for icon indicators
    const programNames = _.uniq(
      this.joHeaders
        .map((joh) => joh.allowInventory ? joh.inventoryProgramName : null)
        .filter((name): name is string => !!name)
    );
    this.inventoryProductTypeMap = new Map();
    await Promise.all(
      programNames.map(async (name) => {
        const ids = await this.dbQueryService.getAccountOwnedProductTypeIds(name);
        this.inventoryProductTypeMap.set(name, new Set(ids));
      })
    );
  }

  private updateBatchState() {
    const total = this.joHeaders.length;
    const incompleteCount = this.joHeaders.filter((joh) => joh.joStatusId === JoStatusCode.OpenProcessIncomplete).length;

    this.canMarkComplete = incompleteCount > 0;
    this.canUncomplete = this.joHeaders.some((joh) => joh.joStatusId === JoStatusCode.OpenProcessComplete);

    if (total === 0) {
      this.batchStatusLabel = "No Job Orders";
    } else if (incompleteCount === 0) {
      this.batchStatusLabel = "Open/Process Complete";
    } else if (incompleteCount === total) {
      this.batchStatusLabel = "Open/Process Incomplete";
    } else {
      this.batchStatusLabel = `Partially Complete (${incompleteCount} of ${total} incomplete)`;
    }
  }

  isInInventoryProgram(jod: JoDetail): boolean {
    const progName = jod.joHeader?.inventoryProgramName;
    if (!progName || !jod.joHeader.allowInventory) return false;
    return this.inventoryProductTypeMap.get(progName)?.has(jod.product?.productTypeId) ?? false;
  }

  noop() {}

  onJohGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        headerName: "Job Order",
        ...NavFns.createIdCellClickedNavProps("id", this.router, "/in-process/manage/job-orders"),
      },
      {
        headerName: "Delete",
        colId: "deleteJoh",
        cellRenderer: TemplateRendererComponent,
        cellRendererParams: {
          ngTemplate: this.deleteJoHeaderFromBatchCell,
        },
      },
      {
        headerName: "Account",
        field: "account.accountName",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Status",
        field: "joStatus.name",
        filter: "agTextColumnFilter",
      },
      { headerName: "J/O Date", field: "crtnTs", filter: "agDateColumnFilter" },
      {
        headerName: "Billing Address",
        field: "billingAddress1",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "City",
        field: "billingCity",
        filter: "agTextColumnFilter",
      },
      { headerName: "State", field: "billingState" },
      { headerName: "EntryDate", field: "entryDate" },
    ];
    this.johGridOptions.getRowId = (params: GetRowIdParams) => params.data?.id?.toString();
    AgFns.initGrid(event, colDefs);
  }

  canDisplayPull(jod: JoDetail) {
    return jod.getUnresolvedQty() > 0 && !jod.isAccountOwned() && this.areAnyItdAvailable(jod);
  }

  canDisplayUndo(jod: JoDetail) {
    return jod.getItemDetailsRcvdQty() > 0 && !jod.isAccountOwned();
  }

  areAnyItdAvailable(jod: JoDetail) {
    // is case currentMatchSummary has not yet been resolved
    if (this.allMatchSummary == null || this.exactMatchSummary == null) return true;
    return this.allMatchSummary.some((x) => x.joDetailId == jod.id);
  }

  calcPullLabel(jod: JoDetail) {
    const label = "Pull";
    if (this.allMatchSummary == null || this.exactMatchSummary == null) return label;
    var allCount = this.allMatchSummary.find((x) => x.joDetailId == jod.id)?.matchingCount ?? 0;
    var exactCount = this.exactMatchSummary.find((x) => x.joDetailId == jod.id)?.matchingCount ?? 0;
    if (allCount > 0) {
      return `${label} -(${exactCount}/${allCount - exactCount})`;
    }
  }

  onJodGridReady(event: GridReadyEvent) {
    const colDefs = [
      // { headerName: 'J/O Detail Id', field: 'id', cellRenderer: 'agGroupCellRenderer' },
      {
        ...AgFns.createButtonProps("", this.noop.bind(this), {
          icon: "pi pi-info-circle",
          canDisplay: (jod: JoDetail) => this.isInInventoryProgram(jod),
          label: "",
          tooltip: "Available in account inventory program",
          colId: "inventoryIndicator",
        }),
        headerName: "",
        sortable: false,
        ...AgFns.fixWidth(45),
      },
      {
        ...AgFns.createButtonProps("", this.applyItemDetails.bind(this), {
          label: "Pull",
          canDisplay: this.canDisplayPull.bind(this),
          calcLabel: this.calcPullLabel.bind(this),
        }),
        minWidth: 110,
      },
      {
        ...AgFns.createButtonProps("", this.unApplyItemDetails.bind(this), {
          canDisplay: this.canDisplayUndo.bind(this),
          label: "View Undo",
        }),
        width: 130,
      },

      {
        headerName: "Inventory Program",
        valueGetter: (params) => {
          const jod = params.data as JoDetail;
          return this.isInInventoryProgram(jod) ? jod.joHeader.inventoryProgramName : "";
        },
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Manufacturer",
        field: "product.productType.manufacturer.name",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Description",
        field: "product.productType.description",
      },
      {
        headerName: "Features",
        field: "product.featureChoicesExtract",
      },
            {
        headerName: "SKU",
        field: "product.productType.style",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Quantity",
        children: [
          {
            headerName: "Order",
            field: "orderQty",
            ...AgFns.fixWidth(100),
          },
          {
            headerName: "Cancel",
            field: "cancelQty",
            ...AgFns.fixWidth(100),
          },
          {
            headerName: "Return",
            field: "returnQty",
            ...AgFns.fixWidth(100),
          },
          {
            headerName: "Applied",
            type: "numericColumn",
            headerTooltip: "(calc) Quantities already pulled or received",
            valueGetter: (params: any) => {
              const jod = <JoDetail>params.data;
              return jod.getItemDetailsRcvdQty();
            },
            ...AgFns.fixWidth(120),  
          },
          {
            headerName: "PO",
            type: "numericColumn",
            headerTooltip: "(calc) P/O Detail quantities not yet received or canceled",
            valueGetter: (params: any) => {
              const jod = <JoDetail>params.data;
              return jod.getPoDetailsRemainingQty();
            },
            ...AgFns.fixWidth(100),
          },
          {
            headerName: "Open",
            type: "numericColumn",
            headerTooltip:
              '(calc) The net quantities remaining after accounting for pulled items and unreceived purchase order details',
            valueGetter: (params: any) => {
              const jod = <JoDetail>params.data;
              return jod.getUnresolvedQty();
            },
            ...AgFns.fixWidth(130),
          },
        ],
      },
      {
        ...AgFns.createCheckboxProps("Barcode Req'd?", "isAccountOwned"),
        editable: false,
        valueGetter: (params) => {
          const jod = <JoDetail>params.data;
          const needsBarcode = jod.itemDetails.some((itd) => itd.barcodePrintStatusId == BarcodePrintStatusEnum.NotYetPrinted);
          return needsBarcode;
        },
      },
      {
        headerName: "Job Order",
        ...NavFns.createIdCellClickedNavProps("joHeaderId", this.router, "/in-process/manage/job-orders"),
      },
    ];
    const sortFields = ["product.productType.manufacturer.name", "product.productType.description", "product.productType.style"];
    const sortModel = sortFields.map((sf) => {
      return { colId: sf, sort: "asc" as const };
    });
    this.jodGridOptions.getRowId = (params: GetRowIdParams) => params.data?.id?.toString();

    AgFns.initGrid(event, colDefs, sortModel);
  }

  updateJodMasterDetail(parentGridOptions: GridOptions) {
    const detailGridOptions = AgFns.createDetailGridOptions();
    detailGridOptions.columnDefs = [
      { headerName: "Addon", field: "addon.nameAndLocation" },
      { headerName: "Addl. Info", field: "additionalInfo" },
      { headerName: "Price/Unit", field: "unitPriceAmt" },
      { ...AgFns.createCellButtonProps("Documents", this.viewDocCell) },
    ];
    AgFns.updateColDefs(detailGridOptions.columnDefs);
    parentGridOptions.detailCellRendererParams = {
      detailGridOptions: detailGridOptions,
      getDetailRowData: (params) => {
        const jod = params.data as JoDetail;
        params.successCallback(jod.joDetailAddons);
      },
    };
  }

  async onJohRowSelected(e: RowSelectedEvent) {
    // check if a deselect event and ignore
    if (!e.node.isSelected()) {
      return;
    }

    const jodGridApi = this.jodGridOptions?.apiX;
    if (jodGridApi == null) {
      return;
    }

    jodGridApi.deselectAll();

    const currentJoh = <JoHeader>e.node.data;
    const currentJohId = currentJoh.id;

    jodGridApi.forEachNode((node) => {
      const jod = <JoDetail>node.data;
      if (jod.joHeaderId == currentJohId) {
        node.setSelected(true);
      }
    });
  }

  canDeleteJoHeaderFromBatch(joh: JoHeader) {
    return joh != null && joh.joStatusId === JoStatusCode.OpenProcessIncomplete;
  }

  @wrapBusy
  async deleteJoHeaderFromBatch(joh: JoHeader) {
    this.toastr.info("Removing Job Order from Pull batch");
    await this.joUtilsService.deleteJoHeaderFromBatch(joh.id);
    this.toastr.success("Job Order removal complete");
    await this.updateForBatch(this.joPullBatchId);
  }

  applyItemDetails(jod: JoDetail) {
    this.router.navigate(["/in-process/manage/jo-pull-apply", this.joPullBatchId, jod.id, this.shouldPrintBarcodes]);
  }

  async unApplyItemDetails(jod: JoDetail) {
    await DialogBaseComponent.open(UnapplyJodDialogComponent, this, {
      joDetail: jod,
    });

    this.johGridOptions.apiX.refreshCells();
    this.jodGridOptions.apiX.refreshCells();
  }

  async changeBatch() {
    const res = await DialogBaseComponent.open(ChangeJoPullBatchDialogComponent, this, {
      joPullBatchId: this.joPullBatchId,
    });
    if (res && res.joPullBatchId != null) {
      await this.updateForBatch(res.joPullBatchId);
    }
  }

  // wrapBusy makes canDeactivate wait for the PO loop. Leaving mid-loop let Job Orders detach the JoHeaders it reads.
  @wrapBusy
  async markBatchComplete() {
    // TODO: check auth here
    const ok = await this.dialogService.yesNoDialog("Mark Batch Complete", "All unpulled items will be submitted for order.  Continue?");
    if (!ok) return;

    this.toastr.info("Marking Batch Complete and creating order detail records");
    // Only advance job orders still Open/Process Incomplete; Closed ones are left alone,
    // and their JoDetails are excluded so no PO is cut against a closed order.
    const joHeadersToAdvance = this.joHeaders.filter((joh) => joh.joStatusId === JoStatusCode.OpenProcessIncomplete);
    const advanceIdSet = new Set(joHeadersToAdvance.map((joh) => joh.id));
    const joDetailsToOrder = this.joDetails.filter((jod) => advanceIdSet.has(jod.joHeaderId));

    const odCount = await this.joUtilsService.createPurchaseOrdersForJoDetails(joDetailsToOrder);

    // Set the status after the PO lines exist, so no save can commit a complete job order without them.
    joHeadersToAdvance.forEach((joh) => {
      joh.joStatusId = JoStatusCode.OpenProcessComplete;
    });

    await this.dbSaveService.saveChanges();
    this.updateBatchState();

    this.johGridOptions.apiX.refreshCells();
    this.jodGridOptions.apiX.refreshCells();
    this.toastr.success(`Created ${odCount} Purchase Order Detail record(s)`);
    this.goBack();
  }

  @wrapBusy
  async uncompleteBatch() {
    const { ok, pohIdSet } = await this.joUtilsService.uncompleteBatch(this.joPullBatchId);

    if (ok) {
      this.poUtilsService.deleteStrandedPoHeaders(pohIdSet);
      this.updateBatchState();
  
    }

    this.johGridOptions.apiX.refreshCells();
    this.jodGridOptions.apiX.refreshCells();
    AgFns.autoSizeAllColumns(this.jodGridOptions);
  }

  canPrintItemDetailBarcodes() {
    return this.joDetails.some((jod) => jod.itemDetails.some((itd) => itd.barcodePrintStatusId == BarcodePrintStatusEnum.NotYetPrinted));
  }

  async printItemDetailBarcodes() {
    const itemDetails = _.flatMap(this.joDetails, (jod) =>
      jod.itemDetails.filter((itd) => itd.barcodePrintStatusId == BarcodePrintStatusEnum.NotYetPrinted),
    );
    const changes = await this.barcodeService.printItemDetailBarcodesAndNewAddonBinLabels(itemDetails);
    if (changes.length > 0) {
      await this.dbSaveService.saveSelectedChanges(changes);
  
    }
  }

  hasAddonDocs(jodAddon: JoDetailAddon) {
    return jodAddon?.addon.addonDocMaps.length > 0;
  }

  async viewAddonDocs(jodAddon: JoDetailAddon) {
    const data: FileViewDialogParams = {
      header: "View Addon Document",
      docs: jodAddon.addon.addonDocMaps.map((x) => x.addonDoc as IDoc),
    };
    await DialogBaseComponent.open(FileViewDialogComponent, this, data);
  }

  updateLocation() {
    const urlTree = this.router.createUrlTree([`/in-process/manage/jo-pull-to-wip/${this.joPullBatchId}`], {
      queryParams: {
        shouldPrintBarcodes: this.shouldPrintBarcodes,
      },
    });
    const url = urlTree.toString();
    this.domainService.location.replaceState(url);
  }
}
